import { useEffect, useState } from "react";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL as string;

/**
 * Lädt die site-content KV-Map und liefert einen Getter mit Fallback.
 *
 * ## Cache-Strategie: stale-while-revalidate über localStorage
 *
 * Damit reloads keinen "Flash of Old Content" (FOOC) zeigen — sichtbares
 * Umschalten vom statischen JS-Bundle-Default auf den CMS-Wert nach ~200ms —
 * wird die letzte erfolgreiche Fetch-Response synchron aus dem localStorage
 * als Initial-State verwendet. Parallel läuft im Hintergrund immer ein Fetch,
 * der bei neuen Daten den State (und Cache) aktualisiert.
 *
 * Cache-TTL: 24h. Ältere Einträge werden ignoriert.
 *
 * ## Live-Invalidation (Event-basiert)
 *
 * Nach einem Admin-Save (PUT /api/admin/site-content/…) ruft der Editor
 * `invalidateSiteContentCache()` auf. Das:
 *   1. Entfernt den localStorage-Eintrag (nächster Reload nutzt frische Werte).
 *   2. Dispatcht ein `hf:site-content-invalidate`-Event auf `window`.
 *   3. Jeder mounted `useSiteContent()`-Hook lauscht darauf und refetcht sofort.
 *
 * Damit ist kein Hard-Reload nötig, damit der Header (oder andere
 * CMS-abhängige Bereiche) neue Werte anzeigt.
 */
const INVALIDATE_EVENT = "hf:site-content-invalidate";

export function useSiteContent() {
    const [items, setItems] = useState<Record<string, unknown> | null>(() => readCache());
    const [tick, setTick] = useState(0);
    useEffect(() => {
        let cancelled = false;
        void (async () => {
            try {
                const res = await fetch(`${BACKEND_URL}/api/site-content`, { cache: "no-store" });
                const data = await res.json();
                if (cancelled) return;
                const map = (data?.items || {}) as Record<string, unknown>;
                setItems(map);
                writeCache(map);
            } catch {
                if (!cancelled && items === null) setItems({});
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [tick]);

    // Live-Invalidation: Admin-Save triggert Event, wir refetchen sofort.
    useEffect(() => {
        if (typeof window === "undefined") return;
        const onInvalidate = () => setTick((t) => t + 1);
        window.addEventListener(INVALIDATE_EVENT, onInvalidate);
        return () => window.removeEventListener(INVALIDATE_EVENT, onInvalidate);
    }, []);
    function get<T>(key: string, fallback: T): T {
        if (!items) return fallback;
        const v = items[key];
        if (v === undefined || v === null) return fallback;
        if (typeof v === "string" && v.trim() === "") return fallback;
        return v as T;
    }
    /**
     * Liest ein Section-JSON. Wenn nicht vorhanden oder kein Objekt → fallback.
     * Für einzelne Felder innerhalb der Section wird die Fallback-Struktur
     * durchgereicht (keine Deep-Merge — der Caller sollte defensive
     * Feld-Zugriffe machen).
     */
    function getObject<T>(key: string, fallback: T): T {
        if (!items) return fallback;
        const v = items[key];
        if (v === undefined || v === null) return fallback;
        if (typeof v !== "object") return fallback;
        return v as T;
    }
    /**
     * Prüft, ob eine Section auf der Live-Seite gerendert werden soll.
     * Semantik: `enabled !== false` — d.h. undefined/missing/true → sichtbar,
     * nur explizites `false` blendet aus. Bestandsdaten ohne Feld bleiben
     * sichtbar (Backwards-Compat mit sämtlichen bereits gespeicherten
     * Section-JSONs, die kein `enabled` gesetzt haben).
     */
    function sectionEnabled(key: string): boolean {
        if (!items) return true;
        const v = items[key];
        if (!v || typeof v !== "object" || Array.isArray(v)) return true;
        return (v as { enabled?: boolean }).enabled !== false;
    }
    return { ready: items !== null, get, getObject, sectionEnabled };
}

const CACHE_KEY = "hf_site_content_v1";
const CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24h

type CachedShape = {
    timestamp: number;
    data: Record<string, unknown>;
};

function readCache(): Record<string, unknown> | null {
    if (typeof window === "undefined") return null;
    try {
        const raw = window.localStorage.getItem(CACHE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as CachedShape;
        if (!parsed || typeof parsed !== "object") return null;
        if (typeof parsed.timestamp !== "number" || typeof parsed.data !== "object") return null;
        if (Date.now() - parsed.timestamp > CACHE_TTL_MS) return null;
        return parsed.data;
    } catch {
        return null;
    }
}

function writeCache(data: Record<string, unknown>) {
    if (typeof window === "undefined") return;
    try {
        const payload: CachedShape = { timestamp: Date.now(), data };
        window.localStorage.setItem(CACHE_KEY, JSON.stringify(payload));
    } catch {
        // Quota / privater Modus / disabled storage — kein Blocker
    }
}

/**
 * Vom Admin-Editor aufrufen, nachdem eine Section gespeichert wurde.
 *
 * 1. Löscht den LocalStorage-Cache, damit ein Reload sofort frische Werte holt.
 * 2. Dispatcht `hf:site-content-invalidate` → mounted Hooks refetchen live.
 */
export function invalidateSiteContentCache() {
    if (typeof window === "undefined") return;
    try {
        window.localStorage.removeItem(CACHE_KEY);
    } catch {
        /* ignore */
    }
    try {
        window.dispatchEvent(new Event(INVALIDATE_EVENT));
    } catch {
        /* ignore */
    }
}

/**
 * Filter-Helper für Repeater-Items mit optionalem `enabled`-Flag.
 * Items ohne Flag oder mit `enabled === true` bleiben sichtbar.
 * Nur explizit `enabled: false` wird ausgeblendet.
 */
export function isEnabled<T>(item: T): boolean {
    if (!item || typeof item !== "object") return true;
    return (item as { enabled?: boolean }).enabled !== false;
}
